import { Link } from '@tanstack/react-router'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { FileText, Lightbulb, User } from 'lucide-react'
import { useRecentActivity } from '../hooks/use-dashboard-stats'

const typeIcons = {
  report: FileText,
  insight: Lightbulb,
  patient: User,
}

const typeLabels = {
  report: 'Relatório',
  insight: 'Insight',
  patient: 'Paciente',
}

export function RecentActivity() {
  const { data: activities, isLoading } = useRecentActivity()

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="p-5 pb-3">
          <CardTitle className="text-base">Atividade recente</CardTitle>
        </CardHeader>
        <CardContent className="p-5 pt-0">
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-start gap-3">
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="flex-1">
                  <Skeleton className="h-4 w-3/4 mb-2" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!activities?.length) {
    return (
      <Card>
        <CardHeader className="p-5 pb-3">
          <CardTitle className="text-base">Atividade recente</CardTitle>
        </CardHeader>
        <CardContent className="p-5 pt-0">
          <p className="text-sm text-muted-foreground text-center py-8">
            Nenhuma atividade recente
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
        <CardHeader className="p-5 pb-3">
          <CardTitle className="text-base">Atividade recente</CardTitle>
        </CardHeader>
        <CardContent className="p-5 pt-0">
        <div className="space-y-4">
          {activities.map((activity) => {
            const Icon = typeIcons[activity.type]
            return (
              <div key={activity.id} className="flex items-start gap-3">
                <div className="rounded-full bg-muted p-2">
                  <Icon className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      {typeLabels[activity.type]}
                    </Badge>
                    {activity.patientName && (
                      <Link
                        to="/patients/$patientId"
                        params={{ patientId: activity.patientId! }}
                        className="text-xs text-primary hover:underline truncate"
                      >
                        {activity.patientName}
                      </Link>
                    )}
                  </div>
                  <p className="text-sm font-medium truncate mt-1">
                    {activity.title}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(activity.timestamp), {
                      addSuffix: true,
                      locale: ptBR,
                    })}
                  </p>
                </div>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}
